import React, { Fragment, useEffect, useState, forwardRef } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  DataGrid,
  GridToolbar,
  GRID_CHECKBOX_SELECTION_COL_DEF,
} from "@mui/x-data-grid";
import {
  Button,
  Snackbar,
  Tooltip,
  Typography,
  FormControl,
  Select,
  InputLabel,
  MenuItem,
  Checkbox,
  ListItemText,
  OutlinedInput,
} from "@mui/material";
import MuiAlert from "@mui/material/Alert";
import CircularProgress from "@mui/material/CircularProgress";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import { useChosenEmployer } from "../contexts/EmployerContext";
import RefreshingIndicator from "./RefreshingIndicator";
import DownloadIcon from "@mui/icons-material/Download";
import HttpService from "../services/HttpService";
import Labels from "../labels/Labels";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import {
  formatPayfileDateRange,
  isPayfileDateRangeWithinLimit,
} from "../util/payfileDateRange";

export default function Payments(props) {
  const {
    isLoading,
    isFetching,
    isError,
    data,
    setPageFunction,
    setFilterStringFunction,
    dataCount,
    isLoadingDataCount,
    title,
    type,
    dateFromValue,
    dateToValue,
    setDateFromValue,
    setDateToValue,
    statusFilter,
    setStatusFilter,
    setEmployerFilter,
    setSubDepartmentFilter,
  } = props;

  const { chosenEmployerId, employerData } = useChosenEmployer();
  const [selectedEmployer, setSelectedEmployer] = useState(null);
  const [selectedSubDepartment, setSelectedSubDepartment] = useState(null);

  const { data: employersData, isLoading: isLoadingEmployers } = useQuery(
    ["allEmployers"],
    async () => {
      const response = await HttpService.getAxiosClient().get(
        window.API_URL + `/employer/all?excludeRelations=true`
      );
      return response;
    },
    { enabled: chosenEmployerId === "ANY" }
  );

  const companyIdForSubDeps =
    chosenEmployerId === "ANY"
      ? selectedEmployer?.idEmployer
      : employerData?.idEmployer ||
        (chosenEmployerId && chosenEmployerId !== "ANY"
          ? chosenEmployerId
          : undefined);

  function isSubdepartmentEmployer(e) {
    if (!e) return false;
    return (
      !!e.parentEmployerId ||
      !!e.parentId ||
      !!e.parent ||
      !!e.isSubdepartment ||
      !!e.isSubDepartment ||
      e.type === "SUBDEPARTMENT" ||
      e.type === "SUB_DEPARTMENT" ||
      e.employerType === "SUBDEPARTMENT" ||
      e.employerType === "SUB_DEPARTMENT"
    );
  }

  const resolvedCompany =
    chosenEmployerId === "ANY"
      ? selectedEmployer ||
        (employersData?.data || []).find(
          (e) => e.idEmployer === companyIdForSubDeps
        )
      : employerData ||
        (employersData?.data || []).find(
          (e) => e.idEmployer === companyIdForSubDeps
        );

  const showSubdepartmentSelector =
    !!companyIdForSubDeps && !isSubdepartmentEmployer(resolvedCompany);

  const { data: subDepartmentsData, isLoading: isLoadingSubDepartments } =
    useQuery(
      ["subDepartments", companyIdForSubDeps],
      async () => {
        const response = await HttpService.getAxiosClient().get(
          `${window.API_URL}/subdepartment/options?companyId=${companyIdForSubDeps}`
        );
        return response;
      },
      {
        enabled:
          !!companyIdForSubDeps && !isSubdepartmentEmployer(resolvedCompany),
      }
    );

  useEffect(() => {
    setSelectedSubDepartment(null);
    setSubDepartmentFilter?.(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyIdForSubDeps]);

  const [select, setSelection] = useState([]);
  const [filter, setFilter] = useState("");

  const onFilterChange = (filter, params) => {
    const filterString = filter.quickFilterValues.join(" ");
    if (filterString === undefined) {
      setFilterStringFunction("");
      setFilter("");
    } else {
      setFilterStringFunction(filterString);
      setFilter(filterString);
    }
    return filter;
  };

  const downloadDetailsMutation = useMutation(
    (params) => {
      const effectiveEmployerId =
        selectedEmployer?.idEmployer ||
        (chosenEmployerId !== "ANY" ? chosenEmployerId : null);
      const effectiveSubDepartmentId = selectedSubDepartment?.id || null;
      let url =
        window.API_URL +
        "/payfile/details/export?filter=" +
        filter +
        "&type=" +
        type;
      const { from, to } = formatPayfileDateRange(dateFromValue, dateToValue);
      if (from) url += `&from=` + from;
      if (to) url += `&to=` + to;
      if (statusFilter && statusFilter.length > 0)
        url += `&status=` + statusFilter.join(",");
      if (effectiveEmployerId) url += `&employerId=` + effectiveEmployerId;
      if (effectiveSubDepartmentId)
        url += `&subDepartmentId=` + effectiveSubDepartmentId;

      return HttpService.getAxiosClient().get(url, { responseType: "blob" });
    },
    {
      onSuccess: (response) => {
        const { headers, data } = response;
        const disposition = headers["content-disposition"];
        let filename = disposition.split(/;(.+)/)[1].split(/=(.+)/)[1];
        if (filename.toLowerCase().startsWith("utf-8''"))
          filename = decodeURIComponent(filename.replace("utf-8''", ""));
        else filename = filename.replace(/['"]/g, "");
        let url = window.URL.createObjectURL(data);
        let a = document.createElement("a");
        a.href = url;
        a.download = filename;
        document.body.appendChild(a); // append the element to the dom
        a.click();
        a.remove(); // afterwards, remove the element
        setFeedback({
          open: true,
          type: "success",
          message: Labels.getLabel(
            "payments.downloadSuccess",
            "The file was download succesfully"
          ),
          onClose: () => {
            setFeedback({ open: false });
          },
        });
      },
      onError: (err) => {
        if (err?.response?.status === 500) {
          setFeedback({
            open: true,
            type: "error",
            message: Labels.getLabel(
              "payments.downloadError",
              "There was an error trying to download the file"
            ),
          });
        } else {
          const msg = err?.response?.data ? err.response.data : err.message;
          setFeedback({
            open: true,
            type: "error",
            message: msg,
          });
        }
      },
    }
  );

  const onDownloadDetailsButtonClick = (e, params) => {
    downloadDetailsMutation.mutate(null);
  };

  const [feedback, setFeedback] = useState({
    open: false,
    type: "error",
    message: "",
  });

  const Alert = forwardRef(function Alert(props, ref) {
    return <MuiAlert elevation={10} ref={ref} variant="filled" {...props} />;
  });

  const handleFeedbackClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setFeedback({ open: false, type: "error", message: "" });
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const dataColumns = [
    {
      field: "id",
      headerName: Labels.getLabel("payments.column.id", "ID"),
      width: 90,
      hidden: true,
    },
    {
      field: "memberId",
      headerName: Labels.getLabel("payments.column.memberId", "Member Id"),
      width: 140,
      renderCell: (params) => {
        return (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              lineHeight: 1.3,
            }}
          >
            <Typography sx={{ fontSize: 11, mb: 0 }} gutterBottom>
              {params.row.memberId}
            </Typography>
            {params.row.companyId && (
              <Typography
                sx={{
                  fontSize: 10,
                  mb: 0,
                  color: "text.secondary",
                  fontWeight: "bold",
                }}
                gutterBottom
              >
                {params.row.companyId}
              </Typography>
            )}
          </div>
        );
      },
    },
    {
      field: "email",
      headerName: Labels.getLabel("payments.column.email", "Name / Email"),
      width: 230,
      renderCell: (params) => {
        return (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              lineHeight: 1.3,
            }}
          >
            <Typography sx={{ fontSize: 11, mb: 0 }} gutterBottom>
              {params.row.fullName}
            </Typography>
            <Typography
              sx={{
                fontSize: 10,
                mb: 0,
                color: "text.secondary",
                fontWeight: "bold",
              }}
              gutterBottom
            >
              {params.row.email}
            </Typography>
          </div>
        );
      },
    },
    {
      field: "amount",
      headerName: Labels.getLabel("payments.column.amount", "Amount"),
      width: 90,
      align: "right",
      valueFormatter: (params) => {
        if (params.value == null) {
          return "";
        }

        const valueFormatted = Number(params.value).toLocaleString("en-US", {
          minimumFractionDigits: 2,
        });
        return `${valueFormatted}`;
      },
    },
    {
      field: "status",
      headerName: Labels.getLabel("payments.column.status", "Status"),
      width: 160,
      renderCell: (params) => {
        return (
          <Typography sx={{ fontSize: 11, mb: 0 }} gutterBottom>
            {params.row.status}
          </Typography>
        );
      },
    },
    {
      field: "updateDate",
      headerName: Labels.getLabel("payments.column.updateDate", "Update Date"),
      width: 130,
      renderCell: (params) => {
        return (
          <Typography sx={{ fontSize: 11, mb: 0 }} gutterBottom>
            {params.row.updateDate}
          </Typography>
        );
      },
    },
    {
      field: "idDescriptor",
      headerName: Labels.getLabel("payments.column.idDescriptor", "Descriptor"),
      width: 200,
      renderCell: (params) => {
        return (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              lineHeight: 1.3,
            }}
          >
            <Typography sx={{ fontSize: 11, mb: 0 }} gutterBottom>
              {params.row.idDescriptor}
            </Typography>
            {params.row.departmentOrCompany && (
              <Typography
                sx={{
                  fontSize: 10,
                  mb: 0,
                  color: "text.secondary",
                  fontWeight: "bold",
                }}
                gutterBottom
              >
                {params.row.departmentOrCompany}
              </Typography>
            )}
          </div>
        );
      },
    },
    {
      field: "location",
      headerName: Labels.getLabel("payments.column.location", "Location"),
      width: 180,
    },
    {
      field: "filename",
      headerName: Labels.getLabel("payments.column.filename", "Batch Id"),
      width: 170,
      renderCell: (params) => {
        return (
          <Tooltip title={params.row.fileName}>
            <span>
              <Typography sx={{ fontSize: 11, mb: 0 }} gutterBottom>
                {params.row.fileName}
              </Typography>
            </span>
          </Tooltip>
        );
      },
    },
    {
      field: "error",
      headerName: Labels.getLabel("payments.column.error", "Error"),
      width: 150,
      renderCell: (params) => {
        return (
          <Tooltip title={params.row.error}>
            <span>
              <Typography sx={{ fontSize: 11, mb: 0 }} gutterBottom>
                {params.row.error}
              </Typography>
            </span>
          </Tooltip>
        );
      },
    },
  ];

  const columns = React.useMemo(
    () => [
      {
        ...GRID_CHECKBOX_SELECTION_COL_DEF,
        headerName: Labels.getLabel(
          "payments.column.changeLocation",
          "Change Location"
        ),
        width: 100,
      },
      ...dataColumns,
    ],
    [dataColumns]
  );

  const handleRowSelection = (rows) => {
    setSelection(rows);
  };

  useEffect(() => {}, [select]);

  return (
    <Fragment>
      <div style={{ marginBottom: "10px" }}>
        <h2 style={{ marginBottom: "6px" }}>{title}</h2>

        <LocalizationProvider dateAdapter={AdapterDayjs}>
          {/* Row 1: dates + status + actions */}
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              gap: "8px",
              flexWrap: "wrap",
              marginBottom: "8px",
            }}
          >
            <DatePicker
              label="Date from:"
              value={dateFromValue}
              onChange={(newValue) => {
                setDateFromValue(newValue);
                // clear end date if it's now outside the 31-day window
                if (
                  newValue &&
                  dateToValue &&
                  !isPayfileDateRangeWithinLimit(newValue, dateToValue)
                ) {
                  setDateToValue(null);
                }
              }}
              slotProps={{
                field: {
                  clearable: true,
                  onClear: () => {
                    const defaultFrom = dayjs().subtract(7, "day");
                    setDateFromValue(defaultFrom);
                    setDateToValue(defaultFrom.add(7, "day"));
                  },
                },
              }}
            />
            <DatePicker
              label="Date to:"
              value={dateToValue}
              onChange={(newValue) => setDateToValue(newValue)}
              slotProps={{
                field: {
                  clearable: true,
                  onClear: () => {
                    const defaultTo = dateFromValue
                      ? dayjs(dateFromValue).add(7, "day")
                      : dayjs();
                    setDateToValue(defaultTo);
                  },
                },
              }}
              minDate={dateFromValue ? dayjs(dateFromValue) : null}
              maxDate={
                dateFromValue ? dayjs(dateFromValue).add(30, "day") : null
              }
              disabled={dateFromValue === null}
            />

            <FormControl sx={{ width: 220 }}>
              <InputLabel id="status-multiple-label">Status</InputLabel>
              <Select
                labelId="status-multiple-label"
                id="status-multiple"
                multiple
                value={statusFilter || []}
                onChange={(e) => setStatusFilter(e.target.value)}
                input={<OutlinedInput label="Status" />}
                renderValue={(selected) => selected.join(", ")}
              >
                {(type === "pending"
                  ? [
                      "INVALID_AMOUNT",
                      "RETURN_TO_EMPLOYER",
                      "LOAD_ERROR",
                      "DATA_INCONSISTENT",
                      "CANCELED",
                      "SEND_INVITE",
                      "READY_TO_PAY",
                      "DATA_EMAIL_MISMATCH",
                      "UNLOAD_PARTIAL_SUCCESS",
                      "UNLOAD_ERROR",
                    ]
                  : [
                      "LOAD_SUCCESS",
                      "INVALID_AMOUNT",
                      "UNLOAD_SUCCESS",
                      "RETURN_TO_EMPLOYER",
                      "LOAD_ERROR",
                      "DATA_INCONSISTENT",
                      "CANCELED",
                      "SEND_INVITE",
                      "READY_TO_PAY",
                      "DATA_EMAIL_MISMATCH",
                      "UNLOAD_PARTIAL_SUCCESS",
                      "UNLOAD_ERROR",
                    ]
                ).map((name) => (
                  <MenuItem key={name} value={name}>
                    <Checkbox
                      checked={(statusFilter || []).indexOf(name) > -1}
                    />
                    <ListItemText primary={name} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {isFetching &&
            !(chosenEmployerId === "ANY" || showSubdepartmentSelector) ? (
              <RefreshingIndicator />
            ) : (
              ""
            )}
            <Button
              style={{ height: "40px" }}
              onClick={onDownloadDetailsButtonClick}
              variant="contained"
              disabled={downloadDetailsMutation.isLoading}
              color={
                downloadDetailsMutation.isLoading ? "secondary" : "primary"
              }
            >
              <DownloadIcon style={{ paddingRight: "5px" }} />
              {downloadDetailsMutation.isLoading
                ? Labels.getLabel("payments.downloading", "Downloading...")
                : Labels.getLabel("payments.exportDetails", "Export Details")}
            </Button>
          </div>

          {/* Row 2: company / subdepartment filters */}
          {(chosenEmployerId === "ANY" || showSubdepartmentSelector) && (
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "center",
                gap: "8px",
                flexWrap: "wrap",
              }}
            >
              {isFetching ? <RefreshingIndicator /> : ""}
              {chosenEmployerId === "ANY" && (
                <Autocomplete
                  sx={{ minWidth: 300 }}
                  options={(employersData?.data || []).filter(
                    (o) => o.idEmployer
                  )}
                  getOptionLabel={(option) =>
                    `${option.description}-[${option.idEmployer}]`
                  }
                  value={selectedEmployer}
                  onChange={(event, newValue) => {
                    setSelectedEmployer(newValue);
                    setEmployerFilter?.(newValue?.idEmployer || null);
                  }}
                  loading={isLoadingEmployers}
                  renderInput={(params) => (
                    <TextField {...params} label="Company" size="small" />
                  )}
                  isOptionEqualToValue={(option, value) =>
                    option.idEmployer === value?.idEmployer
                  }
                />
              )}
              {showSubdepartmentSelector && (
                <>
                  <Autocomplete
                    sx={{ minWidth: 300 }}
                    options={subDepartmentsData?.data || []}
                    getOptionLabel={(option) =>
                      `${option.description}-[${option.id}]`
                    }
                    value={selectedSubDepartment}
                    onChange={(event, newValue) => {
                      setSelectedSubDepartment(newValue);
                      setSubDepartmentFilter?.(newValue?.id || null);
                    }}
                    loading={isLoadingSubDepartments}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Subdepartment"
                        size="small"
                      />
                    )}
                    isOptionEqualToValue={(option, value) =>
                      option.id === value?.id
                    }
                  />
                  {isLoadingSubDepartments && (
                    <CircularProgress size={18} sx={{ alignSelf: "center" }} />
                  )}
                </>
              )}
            </div>
          )}
        </LocalizationProvider>
      </div>

      {isLoading || isLoadingDataCount ? (
        <CircularProgress color="inherit" />
      ) : !isError ? (
        <div style={{ height: 510, width: "100%" }}>
          <DataGrid
            disableSelectionOnClick
            disableColumnFilter
            disableColumnSelector
            disableDensitySelector
            pageSize={10}
            rowsPerPageOptions={[10]}
            columnVisibilityModel={{ id: false }}
            onSelectionModelChange={handleRowSelection}
            selectionModel={select}
            components={{ Toolbar: GridToolbar }}
            componentsProps={{
              toolbar: {
                showQuickFilter: true,
                quickFilterProps: { debounceMs: 800 },
              },
            }}
            rows={data?.data}
            getRowHeight={(params) =>
              params.model?.companyId ||
              params.model?.departmentOrCompany ||
              params.model?.email
                ? 48
                : 35
            }
            columns={columns}
            rowCount={dataCount?.data}
            onPageChange={(newPage) => {
              setPageFunction(newPage);
            }}
            paginationMode="server"
            filterMode="server"
            onFilterModelChange={onFilterChange}
            sx={{
              "& .MuiButton-root": {
                display: "none",
              },
            }}
            keepNonExistentRowsSelected
          />

          <Snackbar
            open={feedback.open}
            autoHideDuration={6000}
            onClose={feedback.onClose ? feedback.onClose : handleFeedbackClose}
            anchorOrigin={{ vertical: "top", horizontal: "center" }}
          >
            <Alert
              onClose={
                feedback.onClose ? feedback.onClose : handleFeedbackClose
              }
              severity={feedback.type}
              sx={{ width: "100%" }}
            >
              {feedback.message}
            </Alert>
          </Snackbar>
        </div>
      ) : (
        <h4>
          {Labels.getLabel(
            "payments.error",
            "An error ocurred, please try again later..."
          )}
        </h4>
      )}
    </Fragment>
  );
}
